import { io } from 'socket.io-client';

export const priceKey = (symbol, market) => `${symbol.toUpperCase()}@${market.toUpperCase()}`;

export class PriceFeedClient {
  constructor(config) {
    this.config = config;
    this.socket = null;
    this.subscriptions = new Map();
    this.listeners = new Map();
    this.statusListeners = new Set();
    this.lastPrices = new Map();
  }

  connect() {
    if (this.socket || !this.config.url) return;
    const url = this.config.url.includes('/price-feed')
      ? this.config.url
      : `${this.config.url.replace(/\/$/, '')}/price-feed`;
    this.socket = io(url, {
      auth: this.config.apiKey ? { apiKey: this.config.apiKey } : undefined,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
    });
    this.socket.on('connect', () => {
      this.notifyStatus(true);
      const symbols = [...this.subscriptions.keys()];
      if (symbols.length) this.socket.emit('subscribe', { symbols });
    });
    this.socket.on('disconnect', (reason) => {
      this.notifyStatus(false);
      if (reason === 'io server disconnect') this.socket?.connect();
    });
    this.socket.on('price-update', (data) => {
      if (!data?.symbol || !data?.market || !Number.isFinite(data.price)) return;
      const key = priceKey(data.symbol, data.market);
      const update = { ...data, timestamp: data.timestamp ?? Date.now(), receivedAt: Date.now() };
      this.lastPrices.set(key, update);
      this.listeners.get(key)?.forEach((listener) => listener(update));
    });
  }

  disconnect() {
    if (!this.socket) return;
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
    this.notifyStatus(false);
  }

  isConnected() {
    return this.socket?.connected ?? false;
  }

  onStatusChange(listener) {
    this.statusListeners.add(listener);
    listener(this.isConnected());
    return () => this.statusListeners.delete(listener);
  }

  subscribe(key, listener) {
    const normalized = key.toUpperCase();
    const listeners = this.listeners.get(normalized) || new Set();
    listeners.add(listener);
    this.listeners.set(normalized, listeners);
    const count = this.subscriptions.get(normalized) || 0;
    this.subscriptions.set(normalized, count + 1);
    if (count === 0 && this.socket?.connected) this.socket.emit('subscribe', { symbol: normalized });
    if (this.lastPrices.has(normalized)) listener(this.lastPrices.get(normalized));

    let active = true;
    return () => {
      if (!active) return;
      active = false;
      listeners.delete(listener);
      if (!listeners.size) this.listeners.delete(normalized);
      const current = this.subscriptions.get(normalized) || 0;
      if (current <= 1) {
        this.subscriptions.delete(normalized);
        if (this.socket?.connected) this.socket.emit('unsubscribe', { symbol: normalized });
      } else {
        this.subscriptions.set(normalized, current - 1);
      }
    };
  }

  getLastPrice(key) {
    return this.lastPrices.get(key.toUpperCase());
  }

  notifyStatus(connected) {
    this.statusListeners.forEach((listener) => listener(connected));
  }
}
